import { Box, Card, Chip, darken, Divider, lighten, ThemeProvider, Typography } from "@mui/material";
import ImageUpload from "../components/ImageUpload";
import axios from "axios";
import { memo, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Theme from "../utils/theme";
import type { RootState } from "../store";
import type { IPotentialRecipe } from "../types/recipe";
import { cleanJsonResponse } from "../utils/helpers/generalHelper";
import { addImageRecipe, generatePotentialImageRecipes, updatePotentialImageRecipes } from "../store/slices/recipes";
import Loading from "../components/Loading";
import GenerationRecipe from "../components/GenerationRecipe";
import { CheckCircleOutline } from "@mui/icons-material";
import PageIndicator from "../components/PageIndicator";

const steps = [0, 1, 2];

const MemoizedPageIndicator = memo(PageIndicator);

const GenerateImage: React.FC = () => {
    const authState = useSelector((state: RootState) => state.auth);
    const isGuest = !authState.account;
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);
    const recipeState = useSelector((state: RootState) => state.recipes);

    const potentialRecipes = recipeState.potentialImage;
    const recentRecipes = recipeState.recentImage;

    const dispatch = useDispatch();

    const [startingTab, setStartingTab] = useState<number>(0);
    const [disabledGenerate, setDisabledGenerate] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewFile, setPreviewFile] = useState<File | null>(null);
    const [imageAddError, setImageAddError] = useState("");
    const [chipClickError, setChipClickError] = useState("");
    const [imageAddLoading, setImageAddLoading] = useState(false);
    const [chipCLickLoading, setChipClickLoading] = useState<number[]>([]);
    const [step, setStep] = useState<number>(recentRecipes?.length > 0 ? 2 : potentialRecipes?.length > 0 ? 1 : 0);

    const handleImageAdd = useCallback(
        (previewUrl: string | null, data: { file: File | null }) => {
            if (!data.file || !previewUrl) return;
            const formData = new FormData();
            formData.append("image", data.file);
            setImageAddError("");
            setImageAddLoading(true);
            setDisabledGenerate(true);
            axios
                .post(`${process.env.REACT_APP_API_URL}/recipes/generate/image`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                .then((res) => {
                    const parsedResponse = JSON.parse(res.data.body);
                    if (parsedResponse.length === 0) {
                        setImageAddError("Sorry, we can't find a recipe for your provided image! Please try another image.");
                    } else {
                        setStep(1);
                    }

                    dispatch(
                        updatePotentialImageRecipes(
                            parsedResponse.map((item: { name: string; description: string }) => {
                                return { ...item, generated: false };
                            })
                        )
                    );

                    setDisabledGenerate(true);
                })
                .catch((error) => {
                    setDisabledGenerate(false);
                    setImageAddError("Error while generating potential recipes. Please try again.");
                })
                .finally(() => {
                    setImageAddLoading(false);
                });
        },
        [dispatch]
    );

    const handleChipClick = useCallback(
        (index: number) => {
            if (potentialRecipes[index].generated) {
                const recentRecipeIndex = recentRecipes.findIndex((item) => item.generationId === potentialRecipes[index].generationId);
                setStartingTab(recentRecipeIndex);
                setStep(2);
                return;
            }

            setChipClickError("");
            setChipClickLoading((prev) => [...prev, index]);

            axios
                .post(`${process.env.REACT_APP_API_URL}/recipes/generate/name`, {
                    recipeName: potentialRecipes[index].name,
                    username: isGuest ? null : authState.account?.username,
                })
                .then((res) => {
                    const cleanedRecipe = cleanJsonResponse(res.data.body.data);
                    const recipeJSON = JSON.parse(cleanedRecipe);

                    dispatch(
                        addImageRecipe({
                            name: recipeJSON.name,
                            description: recipeJSON.description,
                            ingredients: recipeJSON.ingredients,
                            tags: recipeJSON.tags,
                            procedure: recipeJSON.procedure,
                            generationId: res.data.body.generationId,
                        })
                    );

                    dispatch(
                        generatePotentialImageRecipes({
                            generationId: res.data.body.generationId,
                            index: index,
                        })
                    );
                })
                .catch((error) => {
                    console.log(error);
                    setChipClickError("Error while generating recipe. Please try again.");
                })
                .finally(() => {
                    setChipClickLoading((prev) => prev.filter((item) => item !== index));
                });
        },
        [authState.account?.username, dispatch, isGuest, potentialRecipes, recentRecipes]
    );

    const renderPotentialRecipesChips = useMemo(() => {
        if (chipClickError) {
            return (
                <Box sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center", mt: 5 }}>
                    <Typography variant="subtitle1">{chipClickError}</Typography>
                </Box>
            );
        }

        if (!potentialRecipes)
            return <Typography variant="subtitle1">No Potential recipes generated. Please return to the previous step!</Typography>;

        return (
            <>
                {chipClickError !== "" && <Typography variant="subtitle1">{chipClickError}</Typography>}
                {potentialRecipes.map((item: IPotentialRecipe, index) => (
                    <Box
                        key={item.name}
                        sx={{ display: "flex", width: "100%", maxWidth: 800, flexDirection: "column", alignItems: "flex-start", mb: 5 }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", maxWidth: "90vw" }}>
                            <Chip
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: "flex",
                                    "& .MuiChip-label": {
                                        lineHeight: "32px",
                                        px: 1,
                                    },
                                    height: "32px",
                                    m: 0.5,
                                    fontSize: "1em",
                                    backgroundColor: item.generated
                                        ? lighten(theme.palette.success.main, 0.9)
                                        : lighten(theme.palette.primary.main, 0.9),
                                    border: item.generated
                                        ? "1px solid " + theme.palette.success.main
                                        : "1px solid " + theme.palette.primary.main,
                                    color: item.generated
                                        ? darken(theme.palette.success.main, 0.3)
                                        : darken(theme.palette.primary.main, 0.3),
                                    cursor: "pointer",
                                }}
                                size="small"
                                variant="filled"
                                label={item.name}
                                onClick={() => handleChipClick(index)}
                            />
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    ml: 1,
                                }}
                            >
                                {chipCLickLoading.includes(index) && (
                                    <>
                                        <Loading size="20px"></Loading>
                                        <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main }}>
                                            Generating
                                        </Typography>
                                    </>
                                )}

                                {item.generated && (
                                    <>
                                        <CheckCircleOutline sx={{ color: theme.palette.success.main, fontSize: "24px" }} />
                                        <Typography variant="subtitle2" sx={{ color: theme.palette.success.main }}>
                                            Generated
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </Box>

                        <Typography sx={{ pl: 2 }}>{item.description}</Typography>
                    </Box>
                ))}
            </>
        );
    }, [chipCLickLoading, chipClickError, handleChipClick, potentialRecipes, theme.palette.primary.main, theme.palette.success]);

    const renderImageUpload = useMemo(
        () => (
            <Card sx={{ p: 3, border: "2px solid " + theme.palette.primary.main, position: "relative", borderRadius: 6 }}>
                {imageAddError && <Typography sx={{ mt: 2 }}>{imageAddError}</Typography>}

                <Typography variant="h5" sx={{ textAlign: "center" }}>
                    Please upload a photo of a dish below.
                </Typography>

                <ImageUpload
                    type={"Post"}
                    handleImageAdd={handleImageAdd}
                    hideCaption={true}
                    buttonLabel={imageAddLoading ? "Generating" : "Generate"}
                    disabledGenerate={disabledGenerate}
                    setDisabledGenerate={setDisabledGenerate}
                    parentPreviewUrl={previewUrl}
                    setParentPreviewUrl={setPreviewUrl}
                    parentPreviewFile={previewFile}
                    setParentPreviewFile={setPreviewFile}
                    loading={imageAddLoading}
                />
            </Card>
        ),
        [disabledGenerate, handleImageAdd, imageAddError, imageAddLoading, previewFile, previewUrl, theme.palette.primary.main]
    );

    const renderRecentRecipes = useMemo(() => {
        if (!recentRecipes || recentRecipes.length === 0) {
            return (
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                    <Typography variant="subtitle2" sx={{ fontSize: "16px", mt: 5, textAlign: "center" }}>
                        No Recipes Generated!
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontSize: "16px", mt: 5, textAlign: "center" }}>
                        Please go back to the previous steps.
                    </Typography>
                </Box>
            );
        }

        return (
            <>
                <Box sx={{ maxWidth: "1200px" }}>
                    <GenerationRecipe recentRecipes={recentRecipes} type="image" startingTab={startingTab} />
                </Box>
            </>
        );
    }, [recentRecipes, startingTab]);

    return (
        <ThemeProvider theme={theme}>
            <MemoizedPageIndicator steps={steps} setStep={setStep} step={step} />
            <Divider />

            <Box sx={{ mt: 2, px: 2, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                {step === 0 && renderImageUpload}
                {step === 1 && renderPotentialRecipesChips}
            </Box>
            <Box sx={{ mt: 0, px: 2, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                {step === 2 && renderRecentRecipes}
            </Box>
        </ThemeProvider>
    );
};

export default GenerateImage;
